/**
 * Base Axios API client with session and token authentication support
 *
 * Supports both:
 * - Session-based auth (BFF pattern): Credentials sent via httpOnly cookies
 * - JWT token auth (legacy): Bearer token in Authorization header
 *
 * Session-based auth is preferred for browser clients.
 */

import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { config } from '@/configs';
import { useAuthStore } from '@/store/authStore';

/**
 * API path constants for all endpoints
 */
export const API_PATHS = {
  // Auth - Session based (BFF pattern)
  AUTH_LOGIN: '/auth/login',
  AUTH_REGISTER: '/auth/register',
  AUTH_LOGOUT: '/auth/logout',
  AUTH_ME: '/auth/me',
  AUTH_VERIFY_EMAIL: '/auth/verify-email',
  AUTH_VERIFY_EMAIL_RESEND: '/auth/verify-email/resend',
  AUTH_SESSIONS: '/auth/sessions',
  AUTH_SESSION: (id: string) => `/auth/sessions/${id}`,
  AUTH_SESSIONS_TERMINATE_ALL: '/auth/sessions/terminate-all',
  AUTH_SESSIONS_TERMINATE_OTHER: '/auth/sessions/terminate-other',

  // MFA - TOTP
  AUTH_MFA_TOTP_ENROLL: '/auth/mfa/totp/enroll',
  AUTH_MFA_TOTP_VERIFY: '/auth/mfa/totp/verify',
  AUTH_MFA_TOTP_REGENERATE: '/auth/mfa/totp/regenerate',

  // MFA - WebAuthn
  AUTH_MFA_WEBAUTHN_REGISTER_OPTIONS: '/auth/mfa/webauthn/register/options',
  AUTH_MFA_WEBAUTHN_REGISTER_COMPLETE: '/auth/mfa/webauthn/register/complete',
  AUTH_MFA_WEBAUTHN_AUTH_OPTIONS: '/auth/mfa/webauthn/authenticate/options',

  // MFA - Methods Management
  AUTH_MFA_METHODS: '/auth/mfa/methods',
  AUTH_MFA_METHOD: (id: string) => `/auth/mfa/methods/${id}`,

  // MFA - Verification (during login)
  AUTH_MFA_VERIFY: '/auth/mfa/verify',

  // MFA - Recovery Codes
  AUTH_MFA_RECOVERY_VERIFY: '/auth/mfa/recovery/verify',
  AUTH_MFA_RECOVERY_REGENERATE: '/auth/mfa/recovery/regenerate',

  // MFA - Passkey Login
  AUTH_PASSKEY_LOGIN: '/auth/passkey/login',

  // Password management
  AUTH_PASSWORD_RESET: '/auth/password/reset',
  AUTH_PASSWORD_RESET_CONFIRM: '/auth/password/reset/confirm',
  AUTH_PASSWORD_CHANGE: '/auth/password/change',

  // Legacy auth endpoints (JWT-based)
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  LOGOUT: '/auth/logout',
  REFRESH: '/auth/refresh',
  PASSWORD_RESET: '/auth/password-reset',
  PASSWORD_RESET_CONFIRM: '/auth/password-reset/confirm',
  CHANGE_PASSWORD: '/auth/change-password',

  // Dashboard
  DASHBOARD: '/dashboard/stats',
  DASHBOARD_CHARTS: '/dashboard/trends',  // Backend uses /trends for chart data
  DASHBOARD_ACTIVITY: '/dashboard/activity',
  DASHBOARD_GEO: '/dashboard/geo-signals',

  // Detections
  DETECTIONS: '/detections',
  DETECTION: (id: string) => `/detections/${id}`,
  DETECTION_ENRICH: (id: string) => `/detections/${id}/enrich`,
  DETECTION_TRUE_POSITIVE: (id: string) => `/detections/${id}/true-positive`,
  DETECTION_FALSE_POSITIVE: (id: string) => `/detections/${id}/false-positive`,
  DETECTION_TAKEDOWN: (id: string) => `/detections/${id}/takedown`,

  // Takedowns
  TAKEDOWNS: '/takedowns',
  TAKEDOWN: (id: string) => `/takedowns/${id}`,
  TAKEDOWN_RESPONSES: (id: string) => `/takedowns/${id}/responses`,
  TAKEDOWN_STATISTICS: '/takedowns/statistics/summary',
  TAKEDOWN_PENDING: '/takedowns/pending',

  // Campaigns
  CAMPAIGNS: '/campaigns',
  CAMPAIGN: (id: string) => `/campaigns/${id}`,
  CAMPAIGN_SUMMARY: (id: string) => `/campaigns/${id}/summary`,
  CAMPAIGN_INFRASTRUCTURE: (id: string) => `/campaigns/${id}/infrastructure`,
  CAMPAIGN_MERGE: '/campaigns/merge',

  // Botnet intelligence (infrastructure layer)
  BOTNETS: '/botnets',
  BOTNET: (id: string) => `/botnets/${id}`,
  BOTNET_NODES: (id: string) => `/botnets/${id}/nodes`,

  // Phishing-kit intelligence + threat actor layer
  KITS: '/kits',
  KIT: (id: string) => `/kits/${id}`,
  KIT_OVERVIEW: '/kits/overview',
  KIT_FILES: (id: string) => `/kits/${id}/files`,
  KIT_SHARED_FILES: '/kits/shared-files',
  KIT_SHARED_FILE: (sha: string) => `/kits/shared-files/${sha}`,
  ACTORS: '/actors',
  ACTOR: (key: string) => `/actors/${key}`,

  // DMARC — backend uses /unauthorized-sources and /stats; the analysis
  // and sources aggregate endpoints are added in dmarc_reports.py to
  // match the frontend's contract.
  DMARC_REPORTS: '/dmarc/reports',
  DMARC_REPORT: (id: string) => `/dmarc/reports/${id}`,
  DMARC_ANALYSIS: '/dmarc/analysis',
  DMARC_SOURCES: '/dmarc/sources',
  DMARC_SOURCE: (id: string) => `/dmarc/sources/${id}`,
  DMARC_UNAUTHORIZED: '/dmarc/unauthorized-sources',
  DMARC_STATS: '/dmarc/stats',
  DMARC_ALERT: (id: string) => `/dmarc/alerts/${id}`,

  // Users
  USERS: '/users',
  USER: (id: string) => `/users/${id}`,
  USER_ME: '/users/me',

  // Companies
  COMPANIES: '/companies',
  COMPANY: (id: string) => `/companies/${id}`,
  COMPANY_SETTINGS: (id: string) => `/companies/${id}/settings`,

  // SSO Configuration (Admin)
  SSO_CONFIG: '/admin/sso/config',
  SSO_PROVIDERS: '/admin/sso/providers',
  SSO_PROVIDER: (id: string) => `/admin/sso/providers/${id}`,
  SSO_TEST: '/admin/sso/test',

  // SCIM (Provisioning)
  SCIM_CONFIG: '/admin/scim/config',
  SCIM_TOKEN: '/admin/scim/token',

  // API Credentials (Service Users)
  API_CREDENTIALS: '/admin/api-credentials',
  API_CREDENTIAL: (id: string) => `/admin/api-credentials/${id}`,
  API_CREDENTIAL_ROTATE: (id: string) => `/admin/api-credentials/${id}/rotate`,

  // Audit Logs
  AUDIT_LOGS: '/admin/audit',
  AUDIT_LOG: (id: string) => `/admin/audit/${id}`,

  // Onboarding
  ONBOARDING_PROGRESS: '/onboarding/progress',
  ONBOARDING_STEP: (step: string) => `/onboarding/steps/${step}`,
  ONBOARDING_COMPLETE: '/onboarding/complete',
  ONBOARDING_CANCEL: '/onboarding/cancel',
  ONBOARDING_RESUME: '/onboarding/resume',
  ONBOARDING_SKIP: (step: string) => `/onboarding/skip/${step}`,

  // Domain Verification
  DOMAINS: '/domains',
  DOMAIN: (id: string) => `/domains/${id}`,
  DOMAINS_VERIFY: '/domains/verify',
  DOMAINS_VERIFY_CHECK: '/domains/verify/check',

  // API Keys (User-facing)
  API_KEYS: '/api-keys',
  API_KEY: (id: string) => `/api-keys/${id}`,
  API_KEYS_LIMITS: '/api-keys/limits/info',

  // Premium Services
  PREMIUM_SERVICES: '/services',
  PREMIUM_SERVICE: (id: string) => `/services/${id}`,
  PREMIUM_SERVICE_BOOK: (id: string) => `/services/${id}/book`,
  TOKEN_BALANCE: '/tokens/balance',
  TOKEN_ADJUST: '/tokens/adjust',

  // Bookings
  BOOKINGS: '/bookings',
  BOOKING: (id: string) => `/bookings/${id}`,
  BOOKING_CANCEL: (id: string) => `/bookings/${id}/cancel`,
  BOOKING_DELIVERABLE: (bookingId: string, index: number) => `/bookings/${bookingId}/deliverables/${index}`,

  // Quotes
  QUOTES: '/quotes',
  QUOTE_ACCEPT: (id: string) => `/quotes/${id}/accept`,
  QUOTE_DECLINE: (id: string) => `/quotes/${id}/decline`,

  // Admin Premium Services
  ADMIN_SERVICES: '/admin/services',
  ADMIN_SERVICE: (id: string) => `/admin/services/${id}`,
  ADMIN_BOOKINGS: '/admin/bookings',
  ADMIN_BOOKING: (id: string) => `/admin/bookings/${id}`,
  ADMIN_BOOKING_DELIVERABLE: (id: string) => `/admin/bookings/${id}/deliverables`,
} as const;

/**
 * Standard API error response structure
 */
export interface ApiError {
  detail: string;
  code?: string;
  field?: string;
}

/**
 * Paginated response wrapper
 */
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

/**
 * Axios instance configured for the Phishiness API
 *
 * Uses `withCredentials: true` to send session cookies with all requests.
 */
export const api = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  // IMPORTANT: Enable credentials for session-based auth
  withCredentials: true,
});

/**
 * Request interceptor - add auth token if using JWT mode
 *
 * For session-based auth, cookies are sent automatically via withCredentials.
 * JWT tokens are added for backwards compatibility and API key auth.
 */
api.interceptors.request.use(
  (reqConfig: InternalAxiosRequestConfig) => {
    const { accessToken, updateLastActivity } = useAuthStore.getState();

    // Update last activity for session staleness tracking
    updateLastActivity();

    // Add JWT token if present (legacy or API key auth)
    if (accessToken) {
      reqConfig.headers.Authorization = `Bearer ${accessToken}`;
    }

    return reqConfig;
  },
  (error) => Promise.reject(error)
);

/**
 * Endpoints that verify credentials supplied in the request body.
 *
 * A 401 from these means "the credentials you just submitted were wrong", NOT
 * "your session expired". They must skip the refresh-and-retry path: retrying
 * re-submits the request, and in session-cookie mode (no refresh token in the
 * store) the handler falls through to clearAuth() + redirectToLogin() — so
 * mistyping your current password would log you out instead of showing an
 * error.
 */
const CREDENTIAL_CHECK_PATHS = [
  '/auth/login',
  '/auth/password/change',
  '/auth/password/reset/confirm',
  '/auth/mfa/verify',
  '/auth/mfa/recovery/verify',
  '/auth/passkey/login',
];

function isCredentialCheck(url?: string): boolean {
  if (!url) return false;
  const path = url.split('?')[0] ?? url;
  return CREDENTIAL_CHECK_PATHS.some((candidate) => path.endsWith(candidate));
}

/**
 * Response interceptor - handle authentication errors
 *
 * Session-based auth: 401 means session expired, redirect to login
 * JWT auth: 401 triggers token refresh attempt
 * Credential-checking endpoints: 401 is passed through to the caller
 */
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Get auth state
    const { accessToken, refreshToken, setTokens, clearAuth, setMfaRequired } =
      useAuthStore.getState();

    // Handle 401 Unauthorized
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isCredentialCheck(originalRequest?.url)
    ) {
      originalRequest._retry = true;

      // Check if this is an MFA required response
      const errorCode = error.response?.data?.code;
      if (errorCode === 'mfa_required') {
        setMfaRequired(true);
        return Promise.reject(error);
      }

      // If we have a refresh token (JWT mode), try to refresh
      if (refreshToken && accessToken) {
        try {
          const response = await axios.post(
            `${config.apiUrl}/auth/refresh`,
            { refresh_token: refreshToken },
            { withCredentials: true }
          );

          const { access_token, refresh_token: newRefreshToken } = response.data;
          setTokens(access_token, newRefreshToken || refreshToken);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // If 429 (concurrent refresh), wait and retry original request
          // The other refresh request should have succeeded
          if (axios.isAxiosError(refreshError) && refreshError.response?.status === 429) {
            await new Promise(resolve => setTimeout(resolve, 500));
            return api(originalRequest);
          }
          // Refresh failed - clear auth and redirect
          clearAuth();
          redirectToLogin();
          return Promise.reject(error);
        }
      }

      // No refresh token (session-based auth) - clear and redirect
      clearAuth();
      redirectToLogin();
    }

    // Handle 403 Forbidden (cross-tenant access attempt)
    if (error.response?.status === 403) {
      const errorCode = error.response?.data?.code;
      if (errorCode === 'cross_tenant_access') {
        console.error('Cross-tenant access denied');
        // Could redirect to an error page or show notification
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Redirect to login page
 */
function redirectToLogin(): void {
  // Don't redirect if already on auth pages
  if (window.location.pathname.startsWith('/auth/')) {
    return;
  }

  // Store the current URL to redirect back after login
  const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = `/auth/login?return=${returnUrl}`;
}

/**
 * Check if the current session is valid by calling /auth/me
 *
 * This is useful on app initialization to verify the session cookie is valid.
 */
export async function checkSession(): Promise<boolean> {
  try {
    await api.get(API_PATHS.AUTH_ME);
    return true;
  } catch {
    return false;
  }
}

/**
 * Type-safe API request helpers
 */
export const apiHelpers = {
  /**
   * GET request with typed response
   */
  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await api.get<T>(url, { params });
    return response.data;
  },

  /**
   * POST request with typed response
   */
  async post<T, D = unknown>(url: string, data?: D): Promise<T> {
    const response = await api.post<T>(url, data);
    return response.data;
  },

  /**
   * PUT request with typed response
   */
  async put<T, D = unknown>(url: string, data?: D): Promise<T> {
    const response = await api.put<T>(url, data);
    return response.data;
  },

  /**
   * PATCH request with typed response
   */
  async patch<T, D = unknown>(url: string, data?: D): Promise<T> {
    const response = await api.patch<T>(url, data);
    return response.data;
  },

  /**
   * DELETE request
   */
  async delete<T = void>(url: string): Promise<T> {
    const response = await api.delete<T>(url);
    return response.data;
  },
};

/**
 * Translate frontend list filter conventions to backend query params.
 * Frontend uses: limit, offset, sort_order, domain_contains
 * Backend uses:  page_size, page, sort_desc, domain_search
 */
export function toBackendParams(filters: Record<string, unknown>): Record<string, unknown> {
  const params = { ...filters };
  if ('limit' in params) {
    const limit = params.limit as number;
    const offset = (params.offset as number) || 0;
    params.page_size = limit;
    params.page = Math.floor(offset / (limit || 20)) + 1;
    delete params.limit;
    delete params.offset;
  }
  if ('sort_order' in params) {
    if (params.sort_order) params.sort_desc = params.sort_order === 'desc';
    delete params.sort_order;
  }
  if ('domain_contains' in params) {
    params.domain_search = params.domain_contains;
    delete params.domain_contains;
  }
  return params;
}

export default api;
